import { GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { ApiResponse } from '@roc/feature-app-core';
import { AppraisalsService } from '../services/appraisalsService';
import { FileUpload } from '@roc/ui';
import { DocumentService } from '@roc/feature-documents';
import { downloadDocument } from '@roc/feature-utils';

export class AppraisalsStore {
  globalStore: GlobalStore;
  appraisalsService: AppraisalsService;
  loanAppraisalsGridStore: GridStore;
  standaloneAppraisalsGridStore: GridStore;
  loanAppraisalGridType: string = 'COMPLETED';
  loansFromMoveOptions: { label: string; value: string }[] = [];
  selectedLoanId: string = '';
  propertiesOptions: { label: string; value: string }[] = [];
  selectedProperty: string = '';
  documentService: DocumentService;

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.appraisalsService = new AppraisalsService();
    this.documentService = new DocumentService();

    this.loanAppraisalsGridStore = new GridStore(() =>
      this.fetchLoanAppraisals(this.globalStore.isBackOfficeView)
    );
    this.standaloneAppraisalsGridStore = new GridStore(() =>
      this.fetchStandaloneAppraisals()
    );

    makeObservable(this, {
      uploadAppraisalDocument: flow,
      uploadStandaloneValuationDocument: flow,
      setLoanAppraisalGridType: action,
      getValidLoanFromMoveAppraisal: flow,
      loansFromMoveOptions: observable,
      selectedLoanId: observable,
      setSelectedLoanId: action,
      onChangeLoanId: flow,
      createTamariskTaskToMoveLoan: flow,
      propertiesOptions: observable,
      getPropertiesOptions: flow,
      selectedProperty: observable,
      setSelectedProperty: action,
      requestReconsideration: flow,
      downloadAppraisalReport: flow,
    });
  }

  setSelectedProperty(property) {
    this.selectedProperty = property;
  }

  *getPropertiesOptions() {
    try {
      const { data } = yield this.appraisalsService.getPropertiesOptions(
        this.selectedLoanId
      );
      this.propertiesOptions = data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error trying to get properties options.',
      });
    }
  }

  setLoanAppraisalGridType(loanAppraisalGridType: string) {
    this.loanAppraisalGridType = loanAppraisalGridType;
  }

  async fetchLoanAppraisals(isBackOfficeView = false) {
    this.loanAppraisalsGridStore.gridData;
    try {
      const response: ApiResponse = await this.appraisalsService.getLoanAppraisals(
        this.loanAppraisalGridType,
        this.loanAppraisalsGridStore.gridData.meta.pageNumber,
        this.loanAppraisalsGridStore.gridData.meta.pageSize,
        this.loanAppraisalsGridStore.gridData.meta.sortDir,
        this.loanAppraisalsGridStore.gridData.meta.sortBy,
        this.loanAppraisalsGridStore.gridData.meta.filters,
        this.loanAppraisalsGridStore.gridData.meta.dropdownFilters,
        isBackOfficeView
      );
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error fetching appraisals',
      });
    }
  }

  async fetchStandaloneAppraisals() {
    return { data: { data: { rows: [], totalCount: 0 } } } as any;
  }

  *uploadAppraisalDocument(
    appraisalId: number,
    isLoanAppraisal: boolean,
    documentType: string,
    fileUpload: FileUpload
  ) {
    const file = fileUpload.file;
    const blob = file.slice(0, file.size, file.type);
    const newFile = new File([blob], fileUpload.name, { type: file.type });
    yield this.appraisalsService.addAppraisalDocument(
      appraisalId,
      isLoanAppraisal,
      documentType,
      newFile
    );
  }

  *uploadStandaloneValuationDocument(
    loanPropertyValuationId: number,
    isLoanAppraisal: boolean,
    documentType: string,
    fileUpload: FileUpload
  ) {
    const file = fileUpload.file;
    const blob = file.slice(0, file.size, file.type);
    const newFile = new File([blob], fileUpload.name, { type: file.type });
    yield this.appraisalsService.addValuationDocument(
      loanPropertyValuationId,
      isLoanAppraisal,
      documentType,
      newFile
    );
  }

  setSelectedLoanId(value) {
    this.selectedLoanId = value;
    this.getPropertiesOptions();
  }

  onChangeLoanId(value) {
    this.selectedLoanId = value;
  }

  *getValidLoanFromMoveAppraisal(loanId) {
    try {
      const response = yield this.appraisalsService.getValidLoanFromMoveAppraisal(
        loanId
      );
      this.loansFromMoveOptions = response?.data;
    } catch (error) {
      console.log('error ', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error fetching loans options to move appraisal from.',
      });
    }
  }

  *createTamariskTaskToMoveLoan(loanId) {
    try {
      const {
        data,
      } = yield this.appraisalsService.createTamariskTaskToMoveLoan(
        this.selectedLoanId,
        loanId,
        this.selectedProperty
      );
      if (data?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message:
            'Task to tamarisk specialist to move the appraisal was succesfully created!',
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: data?.responseMessage,
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error trying to move appraisal from this loan.',
      });
    }
  }
  *requestReconsideration() {
    try {
      const response = yield this.appraisalsService.requestReconsideration(
        this.selectedLoanId,
        this.selectedProperty
      );
      if (response?.data?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Appraisal reconsideration request submitted successfully!',
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error trying to request reconsideration.',
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error submitting appraisal reconsideration request.',
      });
    }
  }

  *downloadAppraisalReport(propertyId, loanId, propertyAppraisalId) {
    //aqui
    try {
      const { data } = yield this.appraisalsService.getAppraisalReport(
        propertyId,
        loanId,
        propertyAppraisalId
      );
      const response = yield this.documentService.downloadDocument(data);
      downloadDocument(
        response.data,
        response.headers,
        'download',
        data.originalFileName
      );
    } catch (error) {
      console.log('Error', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while downloading the appraisal report file.',
      });
    }
  }
}
